import {
  EntityType,
  GetTaskByIdDocument,
  useUpdateAndCreateSubtasksMutation,
  WorkflowCode,
} from '@exam-management/graphql';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  type DialogProps,
} from '@shared/components';
import { useLogger } from '@shared/logger';
import { useSnackbar } from '@shared/snackbar';
import { useForm, FormProvider } from 'react-hook-form';

import { useQSetReviewTask } from '../../providers';
import { QSetReviewFormUserSelection } from '../qset-review-form-user-selection';

interface ChangeReviewerFormValues {
  reviewer: { label: string; value: string } | null;
}

export interface ChangeReviewerModalProps extends Omit<DialogProps, 'onSubmit' | 'open'> {
  onClose: () => void;
  open?: boolean;
  excludeUserIds?: string[];
  subtaskId: string;
}

export default function ChangeReviewerModal(props: Readonly<ChangeReviewerModalProps>) {
  const { onClose, open = false, excludeUserIds = [], subtaskId } = props;

  const { mainTaskDetail } = useQSetReviewTask();
  const { showSnackbar } = useSnackbar();
  const logger = useLogger();

  const [updateAndCreateSubtasks, { loading: isSubmitting }] = useUpdateAndCreateSubtasksMutation({
    onCompleted: () => {
      showSnackbar({ message: 'Reviewer has been changed', variant: 'success' });
      methods.reset();
      onClose();
    },
    onError: (error) => {
      logger.error(error);
      showSnackbar({ message: 'Failed to change reviewer', variant: 'error' });
    },
    refetchQueries: [
      {
        query: GetTaskByIdDocument,
        variables: {
          getTaskByIdInput: {
            taskId: mainTaskDetail.id,
          },
        },
      },
    ],
  });

  const methods = useForm<ChangeReviewerFormValues>({
    defaultValues: {
      reviewer: null,
    },
  });

  const handleClose = () => {
    methods.reset();
    onClose();
  };

  const handleSubmit = methods.handleSubmit(async (data) => {
    if (!data.reviewer?.value) return;

    const subtask = mainTaskDetail.subtasks.find((s) => s.id === subtaskId);
    if (!subtask) return;

    const updatedSubtasks = mainTaskDetail.subtasks.map((s) => ({
      id: s.id,
      assigneeId: s.id === subtaskId ? (data.reviewer?.value ?? '') : (s.assignee?.id ?? ''),
      dueDate: s.dueDate ?? new Date(),
      entityType: EntityType.Qset,
      entityId: s.qsetId,
    }));

    await updateAndCreateSubtasks({
      variables: {
        updateAndCreateSubtasksInput: {
          mainTaskId: mainTaskDetail.id,
          workflowCode: WorkflowCode.ReviewQSet,
          subtasks: updatedSubtasks,
        },
      },
    });
  });

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='sm'
      fullWidth
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <DialogTitle onClose={handleClose}>Change Reviewer</DialogTitle>
      <Divider />
      <DialogContent>
        <FormProvider {...methods}>
          <QSetReviewFormUserSelection name='reviewer' excludeUserIds={excludeUserIds} />
        </FormProvider>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button variant='outlined' color='secondary' onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button variant='contained' onClick={handleSubmit} loading={isSubmitting}>
          Change Reviewer
        </Button>
      </DialogActions>
    </Dialog>
  );
}
